import {
  formatAlertLocation,
  formatAlertType,
  humanizeLabel,
  isSosAlert,
  type AlertRow,
} from "./alert-queue";
import type { Channel } from "./channels";
import type { FeedPost } from "./feed";
import type { MessageThread } from "./messages";

export const INBOX_SEGMENTS = ["all", "critical", "action", "boards", "messages"] as const;
export type InboxSegment = (typeof INBOX_SEGMENTS)[number];
export type InboxRowKind = "sos" | "feed_ack" | "board" | "message";

interface InboxRowBase {
  readonly key: string;
  readonly kind: InboxRowKind;
  readonly occurredAt: string | null;
  readonly title: string;
  readonly summary: string;
}

export interface CriticalInboxRow extends InboxRowBase {
  readonly kind: "sos";
  readonly alertEventId: string;
  readonly alertType: string;
  readonly status: string;
  readonly isOpen: boolean;
  /**
   * True for a life-safety press, false for every other critical alert. Decided once here so
   * the ordering and the row's route cannot disagree about what an SOS is.
   */
  readonly isSos: boolean;
  readonly hostHref: string;
  readonly mapHref: string | null;
}

export interface ActionInboxRow extends InboxRowBase {
  readonly kind: "feed_ack";
  readonly postId: string;
  readonly body: string;
  readonly audienceLabel: string;
  readonly authorDisplayName: string;
  readonly requiresAck: boolean;
  readonly acknowledgementCount: number;
  readonly hostHref: string;
}

export interface BoardInboxRow extends InboxRowBase {
  readonly kind: "board";
  readonly channelId: string;
  readonly unread: boolean;
  readonly hostHref: string;
}

export interface MessageInboxRow extends InboxRowBase {
  readonly kind: "message";
  readonly threadId: string;
  readonly threadKind: MessageThread["kind"];
  readonly memberCount: number;
  readonly unread: boolean;
  readonly callerCanSend: boolean;
  readonly hostHref: string;
}

export type InboxRow = CriticalInboxRow | ActionInboxRow | BoardInboxRow | MessageInboxRow;

export interface InboxBadgeCounts {
  readonly critical: number;
  readonly action: number;
  readonly boards: "dot" | "none";
  readonly messages: "dot" | "none";
}

const SECTION_PRIORITY: Readonly<Record<InboxRowKind, number>> = {
  sos: 0,
  feed_ack: 1,
  board: 2,
  message: 3,
};

function timeValue(value: string | null): number {
  const parsed = Date.parse(value ?? "");
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Ordering only, within the one Critical band the shell already has. A life-safety press
 * outranks every other critical alert regardless of age, because a newer geofence alert
 * pushing an open SOS down the list is the failure this ordering exists to prevent. Below
 * that first key, time still decides. Inbox gains no act and no second band from this.
 */
function criticalOrder(left: CriticalInboxRow, right: CriticalInboxRow): number {
  if (left.isSos !== right.isSos) return left.isSos ? -1 : 1;
  return timeValue(right.occurredAt) - timeValue(left.occurredAt);
}

/** Critical always leads, then Action, Boards, and Messages. Time orders only within a band. */
export function sortInboxRows(rows: readonly InboxRow[]): InboxRow[] {
  return [...rows].sort((left, right) => {
    const sectionOrder = SECTION_PRIORITY[left.kind] - SECTION_PRIORITY[right.kind];
    if (sectionOrder !== 0) return sectionOrder;
    if (left.kind === "sos" && right.kind === "sos") return criticalOrder(left, right);
    return timeValue(right.occurredAt) - timeValue(left.occurredAt);
  });
}

function alertHostHref(alert: AlertRow, focusMap = false): string {
  const params = new URLSearchParams({
    cad: isSosAlert(alert.alertType) ? "sos" : "alerts",
    alert: alert.alertEventId,
  });
  if (focusMap) params.set("focus", "map");
  return `/?${params.toString()}`;
}

export function criticalInboxRows(alerts: readonly AlertRow[]): CriticalInboxRow[] {
  return alerts
    .filter(
      (alert) =>
        alert.alertEventId.length > 0 &&
        (alert.status === "open" || alert.status === "acknowledged"),
    )
    .map((alert) => {
      const location = formatAlertLocation(alert);
      const officer = alert.guardLabel ?? "Officer not identified";
      const canFocusMap = alert.routeAssignmentId !== null || alert.siteId !== null;
      return {
        key: `sos:${alert.alertEventId}`,
        kind: "sos" as const,
        alertEventId: alert.alertEventId,
        alertType: alert.alertType,
        status: alert.status,
        isOpen: alert.status === "open",
        isSos: isSosAlert(alert.alertType),
        occurredAt: alert.detectedAt,
        title: `${formatAlertType(alert.alertType)} · ${location}`,
        summary: `${officer} · ${humanizeLabel(alert.status)}`,
        hostHref: alertHostHref(alert),
        mapHref: canFocusMap ? alertHostHref(alert, true) : null,
      };
    })
    .sort(criticalOrder);
}

export function actionInboxRows(posts: readonly FeedPost[]): ActionInboxRow[] {
  return posts
    .map((post) => ({
      key: `feed_ack:${post.postId}`,
      kind: "feed_ack" as const,
      postId: post.postId,
      body: post.body,
      audienceLabel: post.audienceLabel,
      authorDisplayName: post.authorDisplayName,
      occurredAt: post.publishedAt,
      title: post.title,
      summary: post.requiresAck
        ? `${post.ackCount} acknowledged · acknowledgement required`
        : `${post.ackCount} acknowledged · Feed post`,
      requiresAck: post.requiresAck,
      acknowledgementCount: post.ackCount,
      hostHref: `/inbox/action?post=${encodeURIComponent(post.postId)}`,
    }))
    .sort((left, right) => {
      if (left.requiresAck !== right.requiresAck) return left.requiresAck ? -1 : 1;
      return timeValue(right.occurredAt) - timeValue(left.occurredAt);
    });
}

function boardKindLabel(channel: Channel): string {
  if (channel.kind === "account") return "Company board";
  if (channel.kind === "site") return "Site board";
  return "Shift group board";
}

export function boardInboxRows(channels: readonly Channel[]): BoardInboxRow[] {
  return channels
    .map((channel) => ({
      key: `board:${channel.channelId}`,
      kind: "board" as const,
      channelId: channel.channelId,
      occurredAt: channel.lastMessageAt ?? channel.lastPostAt ?? channel.createdAt,
      title: channel.name,
      summary: boardKindLabel(channel),
      unread: channel.unread,
      hostHref: `/inbox/boards?channel=${encodeURIComponent(channel.channelId)}`,
    }))
    .sort((left, right) => timeValue(right.occurredAt) - timeValue(left.occurredAt));
}

export function messageInboxRows(threads: readonly MessageThread[]): MessageInboxRow[] {
  return threads
    .map((thread) => {
      const kindLabel = thread.kind === "direct" ? "Direct" : `Crew · ${thread.memberCount} members`;
      return {
        key: `message:${thread.threadId}`,
        kind: "message" as const,
        threadId: thread.threadId,
        threadKind: thread.kind,
        memberCount: thread.memberCount,
        occurredAt: thread.lastPostAt ?? thread.createdAt,
        title: thread.name,
        summary:
          thread.lastPostBody === null
            ? `${kindLabel} · No messages yet`
            : `${thread.lastPostAuthorDisplayName ?? "Officer"} · ${thread.lastPostBody}`,
        unread: thread.unreadPostCount > 0,
        callerCanSend: thread.callerCanSend,
        hostHref: `/inbox/messages?thread=${encodeURIComponent(thread.threadId)}`,
      };
    })
    .sort((left, right) => timeValue(right.occurredAt) - timeValue(left.occurredAt));
}

export function buildInboxRows({
  alerts,
  posts,
  channels,
  threads = [],
}: {
  readonly alerts: readonly AlertRow[];
  readonly posts: readonly FeedPost[];
  readonly channels: readonly Channel[];
  readonly threads?: readonly MessageThread[];
}): InboxRow[] {
  return sortInboxRows([
    ...criticalInboxRows(alerts),
    ...actionInboxRows(posts),
    ...boardInboxRows(channels),
    ...messageInboxRows(threads),
  ]);
}

export function inboxBadgeCounts(rows: readonly InboxRow[]): InboxBadgeCounts {
  return {
    critical: rows.filter((row) => row.kind === "sos" && row.isOpen).length,
    action: rows.filter((row) => row.kind === "feed_ack" && row.requiresAck).length,
    boards: rows.some((row) => row.kind === "board" && row.unread) ? "dot" : "none",
    messages: rows.some((row) => row.kind === "message" && row.unread) ? "dot" : "none",
  };
}

export function rowsForInboxSegment(
  rows: readonly InboxRow[],
  segment: InboxSegment,
): InboxRow[] {
  if (segment === "all") return sortInboxRows(rows);
  // The band carries its own ordering, so opening Critical directly cannot show a different
  // sequence from the one All shows for the same rows.
  if (segment === "critical") {
    return rows.filter((row): row is CriticalInboxRow => row.kind === "sos").sort(criticalOrder);
  }
  if (segment === "action") return rows.filter((row) => row.kind === "feed_ack");
  if (segment === "boards") return rows.filter((row) => row.kind === "board");
  return rows.filter((row) => row.kind === "message");
}
